// src/pages/Main/frontCards.js

import React from "react";
import { Link } from "react-router-dom";
import { Badge, Card } from "react-bootstrap";
import FrontShot from "pages/Main/frontShot";
import { COMPOSE_DOMAINS, COMPOSE_PARTS } from "pages/Main/frontContent";
import "pages/Main/frontCards.css";

export const Pill = ({ className, children }) => (
  <Badge className={["title-tag-badge", className].filter(Boolean).join(" ")}>{children}</Badge>
);
export const MutedPill = ({ children }) => (
  <Badge variant="secondary" className="title-tag-badge-muted">{children}</Badge>
);
/** An app's status, `[label, variant]`: the "muted" variant wears the muted pill, any other the plain one. */
export const StatusPill = ({ status: [label, variant] }) => (
  variant === "muted" ? <MutedPill>{label}</MutedPill> : <Pill>{label}</Pill>
);
const Code = ({ children }) => <code className="fp-card-code">{children}</code>;

// Every card that shows a picture is itself a link, so the picture never opens at full size: a click navigates.
const Shot = ({ name, alt }) => (
  <div className="fp-card-img">
    <FrontShot name={name} alt={alt} zoomable={false} />
  </div>
);

const Pills = ({ items, muted }) => (
  <div className="fp-card-pills">
    {items.map((item) => (muted ? <MutedPill key={item}>{item}</MutedPill> : <Pill key={item}>{item}</Pill>))}
  </div>
);

const Body = ({ title, text, children }) => (
  <Card.Body className="fp-card-body">
    <Card.Title as="h3" className="fp-card-title">{title}</Card.Title>
    <Card.Text className="fp-card-text">{text}</Card.Text>
    {children}
  </Card.Body>
);

export const AppCard = ({ app }) => (
  <Card as={Link} to={app.path} className="fp-card fp-card-link">
    <Shot name={app.shots[0].name} alt={app.shots[0].alt} />
    <Body title={app.title} text={app.text}>
      <div className="fp-card-meta">
        <StatusPill status={app.status} />
        <MutedPill>{app.domain}</MutedPill>
        <Code>{app.widget}</Code>
      </div>
    </Body>
  </Card>
);

export const ComposeCard = () => (
  <Card as={Link} to="/apps" className="fp-card fp-card-link">
    <div className="fp-card-img">
      <div className="fp-compose" aria-hidden="true">
        <Pills items={COMPOSE_PARTS} />
        <span className="fp-compose-arrow">&darr;</span>
        <div className="fp-compose-app">
          <span className="fp-eyebrow">Your app</span>
          <Pills items={COMPOSE_DOMAINS} muted />
        </div>
      </div>
    </div>
    <Body
      title="Your app"
      text={"Compose your own from the same parts: a workflow for the work, a schedule to run it, policies for "
        + "what agents may touch and a widget to watch it. It is governed and audited like the two beside it."}>
      <div className="fp-card-meta">
        <Pill>build your own</Pill>
        <MutedPill>Any team</MutedPill>
      </div>
    </Body>
  </Card>
);

export const GalleryCard = ({ item }) => (
  <Card as={Link} to={item.path} className="fp-card fp-card-link">
    <Shot name={item.shot} alt={`${item.title} workflow graph.`} />
    <Body title={item.title} text={item.text}>
      <div className="fp-card-meta">
        <Pill>public</Pill>
        <Code>{item.path.replace(/^\//, "")}</Code>
      </div>
    </Body>
  </Card>
);

export const LabCard = ({ item }) => (
  <Card as={Link} to={item.path} className="fp-card fp-card-link">
    <Shot name={item.shot} alt={`${item.title} in BlobHub.`} />
    <Body title={item.title} text={item.text}>
      {item.tags.length > 0 && (
        <div className="fp-card-pills">
          {item.tags.map((tag) => <Badge key={tag} className="fp-card-tag">{tag}</Badge>)}
        </div>
      )}
      <div className="fp-card-meta">
        <Code>{item.type}</Code>
      </div>
    </Body>
  </Card>
);

export const ExampleCard = ({ example }) => (
  <Card className="fp-card">
    <Card.Header className="fp-card-header">
      {example.domain}
      <span className="fp-card-reset">example</span>
    </Card.Header>
    <Card.Body className="fp-card-body">
      <Card.Title as="h3" className="fp-card-title fp-card-title-plain">{example.title}</Card.Title>
      <Card.Text className="fp-card-text">{example.text}</Card.Text>
      <dl className="fp-props">
        <div>
          <dt>Govern</dt>
          <dd>{example.govern}</dd>
        </div>
      </dl>
    </Card.Body>
  </Card>
);
